import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RouterProvider } from '@tanstack/react-router';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { QueryClientProvider } from '@tanstack/react-query';
import { router } from './router';
import { queryClient } from '@/lib/query-client';
import { Toaster } from './components/ui/sonner';
import { MeProvider } from '@/lib/me-provider';
import { RevocationWatcher } from '@/lib/auth/revocation-watcher';
import { AuthProvider, useAuth } from '@/lib/auth/use-auth';

/**
 * Renders the router only once the session bootstrap has resolved, so route
 * `beforeLoad` guards see a settled `auth` (never the transient 'initializing'
 * phase). The live auth snapshot is injected into the router context here.
 */
function AppRouter() {
  const auth = useAuth();
  const { t } = useTranslation();

  // The single reactive bridge between auth state and routing: whenever the auth
  // phase changes (login, logout, session expiry), re-run the route `beforeLoad`
  // guards against the fresh `auth` snapshot. The guards are the sole authority
  // on where to send the user — nothing else navigates on auth changes.
  useEffect(() => {
    if (auth.phase === 'initializing') {
      return;
    }
    void router.invalidate();
  }, [auth.phase]);

  if (auth.phase === 'initializing') {
    return <div className='flex h-svh items-center justify-center text-muted-foreground'>{t('common:loading')}</div>;
  }

  return <RouterProvider router={router} context={{ queryClient, auth, ensureProfile: auth.ensureProfile }} />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <MeProvider>
          <RevocationWatcher />
          <AppRouter />

          {/*
            The vendored `ui/sonner.tsx` defaults to `theme="system"` via
            next-themes, which would make toasts follow the OS dark setting.
            It spreads `{...props}` after its own `theme`, so pinning it here
            keeps toasts light without editing the vendored file.
          */}
          <Toaster position='top-right' theme='light' />
          <ReactQueryDevtools buttonPosition='bottom-right' initialIsOpen={false} />
        </MeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
